import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import {
  ChevronDownIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";
import { Listbox } from "@headlessui/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import { getMarkdownComponents } from "../utils/markdown";
import { getTextAlignment } from "../utils/helpers";
import grayBackground from "../images/gray-background.png";

// MultiSelect Component
function MultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder,
  label,
}) {
  const [selected, setSelected] = useState(selectedValues || []);

  // Update local state when selectedValues prop changes
  useEffect(() => {
    setSelected(selectedValues || []);
  }, [selectedValues]);

  const handleSelectionChange = (newSelection) => {
    setSelected(newSelection);
    onChange(newSelection);
  };

  const toggleOption = (optionValue) => {
    const newSelection = selected.includes(optionValue)
      ? selected.filter((item) => item !== optionValue)
      : [...selected, optionValue];
    handleSelectionChange(newSelection);
  };

  return (
    <div className="relative">
      <Listbox value={selected} onChange={handleSelectionChange}>
        <div className="relative">
          <Listbox.Button className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left">
            <span className="block truncate">
              {selected.length === 0 ? placeholder : selected.join(", ")}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => {
              const optionValue = option.description || option;
              const isSelected = selected.includes(optionValue);

              return (
                <div
                  key={option.id || option}
                  className="relative cursor-pointer select-none py-2 px-3 hover:bg-primary-100 hover:text-primary-900"
                  onClick={() => toggleOption(optionValue)}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="block truncate">{optionValue}</span>
                  </div>
                </div>
              );
            })}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

// Sortable Task Component
function SortableTask({
  task,
  isEditing,
  editingTaskData,
  setEditingTaskData,
  handleEditTask,
  handleDeleteTask,
  handleSaveEditTask,
  handleSaveEditTaskAndNew,
  handleCancelEditTask,
  handleTaskImageUpload,
  handleRemoveTaskImage,
  taskTypes,
  openVideoDialog,
  handleOpenPreview,
  canPreview,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div
        className="p-4 border border-gray-200 rounded-md bg-white"
        style={style}
      >
        {/* Edit Mode - Two Row Layout */}
        {/* First Row: Task Type, Title, and Action Buttons */}
        <div className="flex items-center space-x-4 mb-4">
          {/* Task Type Dropdown */}
          <div className="w-1/4">
            <select
              value={
                editingTaskData.media_type
                  ? taskTypes.find(
                      (t) => t.media_type === editingTaskData.media_type
                    )?.value || ""
                  : ""
              }
              onChange={(e) => {
                const selectedType = taskTypes.find(
                  (type) => type.value === e.target.value
                );
                if (selectedType) {
                  setEditingTaskData({
                    ...editingTaskData,
                    media_type: selectedType.media_type,
                    title: selectedType.defaultTitle,
                  });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select task type...</option>
              {taskTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title Input */}
          <div className="w-1/4">
            <input
              type="text"
              value={editingTaskData.title}
              onChange={(e) =>
                setEditingTaskData({
                  ...editingTaskData,
                  title: e.target.value,
                })
              }
              placeholder="Task title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={handleSaveEditTaskAndNew}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm"
            >
              Save & New
            </button>
            <button
              onClick={handleSaveEditTask}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={handleCancelEditTask}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Second Row: Text Detail, Text Preview, Image Upload/Preview */}
        {(editingTaskData.media_type === "image" ||
          editingTaskData.media_type === "text") && (
          <>
            <div className="flex space-x-4 h-64">
              {/* Text Detail */}
              <div className="w-2/5">
                <textarea
                  value={editingTaskData.text_detail}
                  onChange={(e) =>
                    setEditingTaskData({
                      ...editingTaskData,
                      text_detail: e.target.value,
                    })
                  }
                  placeholder="Text Detail"
                  className="w-full h-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Text Preview */}
              <div className="w-2/5">
                <div className="w-full h-full bg-gray-50 rounded border border-gray-200 p-2 overflow-y-auto">
                  {editingTaskData.text_detail ? (
                    <div
                      className={`prose prose-sm max-w-none ${getTextAlignment(
                        editingTaskData.text_alignment,
                        editingTaskData.text_detail
                      )}`}
                    >
                      <ReactMarkdown components={getMarkdownComponents()}>
                        {editingTaskData.text_detail}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 text-center">
                      Text preview will appear here...
                    </div>
                  )}
                </div>
              </div>

              {/* Image Preview and Upload */}
              <div className="w-1/5">
                <div className="space-y-2 h-full flex flex-col">
                  {/* Image Preview Area */}
                  <div className="flex-1 bg-gray-200 rounded border-2 border-dashed border-gray-300 flex items-center justify-center relative">
                    {editingTaskData.image_path ? (
                      <>
                        <img
                          src={editingTaskData.image_path}
                          alt="Task preview"
                          className="w-full h-full object-cover rounded"
                        />
                        <button
                          onClick={() => handleRemoveTaskImage(true)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold hover:bg-red-600 shadow-lg"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">
                        Image preview
                      </span>
                    )}
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleTaskImageUpload(e, true)}
                    className="hidden"
                    id={`edit-task-image-upload-${task.id}`}
                  />
                  <label
                    htmlFor={`edit-task-image-upload-${task.id}`}
                    className="bg-primary-600 text-white px-3 py-2 rounded text-sm hover:bg-primary-700 transition-colors cursor-pointer text-center"
                  >
                    Upload Image
                  </label>
                </div>
              </div>
            </div>

            {/* Third Row: Text Alignment Controls and Preview */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() =>
                    setEditingTaskData({
                      ...editingTaskData,
                      text_alignment: "none",
                    })
                  }
                  title="No Alignment (uses word count: <30 words = center, ≥30 words = left, or bullets = left)"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    editingTaskData.text_alignment === "none"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  None
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setEditingTaskData({
                      ...editingTaskData,
                      text_alignment: "center",
                    })
                  }
                  title="Center Alignment"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    editingTaskData.text_alignment === "center"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Center
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setEditingTaskData({
                      ...editingTaskData,
                      text_alignment: "left",
                    })
                  }
                  title="Left Alignment"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    editingTaskData.text_alignment === "left"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Left
                </button>
              </div>
              <button
                type="button"
                onClick={() => handleOpenPreview(editingTaskData)}
                disabled={!canPreview(editingTaskData)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  canPreview(editingTaskData)
                    ? "bg-primary-600 text-white hover:bg-primary-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Preview
              </button>
            </div>
          </>
        )}

        {/* Video Mode */}
        {editingTaskData.media_type === "video" && (
          <div className="flex space-x-4 h-64">
            <div className="w-1/2">
              <button
                type="button"
                onClick={openVideoDialog}
                className="w-full h-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left bg-white hover:bg-gray-50 flex items-center justify-center"
              >
                {editingTaskData.video_url ? "Change Video" : "Select Video..."}
              </button>
            </div>
            <div className="w-1/2">
              {editingTaskData.video_url && (
                <div className="w-full h-full bg-gray-50 rounded border border-gray-200 p-2 flex items-center justify-center">
                  <div className="text-sm text-gray-600">Video selected</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="p-4 border border-gray-200 rounded-md bg-white"
      style={style}
    >
      <div className="flex items-center space-x-4">
        {/* Grabber */}
        <div
          className="text-gray-400 cursor-move"
          ref={setNodeRef}
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="w-5 h-5" />
        </div>

        {/* Task Info */}
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{task.title}</h4>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditTask(task.id)}
            className="text-primary-600 hover:text-primary-900 transition-colors p-1"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDeleteTask(task.id)}
            className="text-red-600 hover:text-red-900 transition-colors p-1"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function LessonEntry() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("lesson");
  const [lesson, setLesson] = useState(null);
  const [lessonTasks, setLessonTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [topics, setTopics] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [guideInstructions, setGuideInstructions] = useState("");
  const [isPreview, setIsPreview] = useState(true);

  // Collections data
  const [ageGroupsData, setAgeGroupsData] = useState([]);
  const [topicsData, setTopicsData] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [existingCoverImage, setExistingCoverImage] = useState("");

  // Artifact state
  const [artifactImage, setArtifactImage] = useState(null);
  const [artifactImagePreview, setArtifactImagePreview] = useState("");
  const [existingArtifactImage, setExistingArtifactImage] = useState("");

  // Task management state
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [newTaskMode, setNewTaskMode] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    media_type: "",
    title: "",
    text_detail: "",
    image_path: "",
    video_url: "",
    text_alignment: "none",
  });
  const [editingTaskData, setEditingTaskData] = useState({
    media_type: "",
    title: "",
    text_detail: "",
    image_path: "",
    video_url: "",
    text_alignment: "none",
  });
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [cachedVideos, setCachedVideos] = useState([]);
  const [showAddVideosDialog, setShowAddVideosDialog] = useState(false);
  const [videosToAdd, setVideosToAdd] = useState([]);
  const [showTaskMenu, setShowTaskMenu] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTaskData, setPreviewTaskData] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch age groups from Firestore
  useEffect(() => {
    const fetchAgeGroups = async () => {
      try {
        const ageGroupsRef = collection(db, "age_groups");
        const q = query(ageGroupsRef, orderBy("order"));
        const querySnapshot = await getDocs(q);

        const ageGroupsList = [];
        querySnapshot.forEach((doc) => {
          ageGroupsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setAgeGroupsData(ageGroupsList);
      } catch (error) {
        console.error("Error fetching age groups:", error);
      }
    };

    fetchAgeGroups();
  }, []);

  // Fetch topics from Firestore
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const unitsRef = collection(db, "units");
        const q = query(unitsRef, orderBy("description"));
        const querySnapshot = await getDocs(q);

        const topicsList = [];
        querySnapshot.forEach((doc) => {
          topicsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setTopicsData(topicsList);
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };

    fetchTopics();
  }, []);

  // Close task menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTaskMenu && !event.target.closest(".task-menu-container")) {
        setShowTaskMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTaskMenu]);

  // Fetch lesson data
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        if (lessonId === "new") {
          setLesson({ id: "new" });
          setLoading(false);
          return;
        }

        const lessonRef = doc(db, "node_lessons", lessonId);
        const lessonSnap = await getDoc(lessonRef);

        if (lessonSnap.exists()) {
          const lessonData = {
            id: lessonSnap.id,
            ...lessonSnap.data(),
          };
          setLesson(lessonData);
          setTitle(lessonData.title || "");
          setDescription(lessonData.description || "");
          setTopics(lessonData.units || []);
          setAgeGroups(lessonData.age_groups || []);
          setGuideInstructions(lessonData.activity_instructions || "");
          setIsPreview(lessonData.isPreview === true);
          // Set existing cover image if available
          if (lessonData.cover_image) {
            setCoverImagePreview(lessonData.cover_image);
            setExistingCoverImage(lessonData.cover_image);
          }
          // Set existing artifact image if available
          if (lessonData.artifact_image) {
            setArtifactImagePreview(lessonData.artifact_image);
            setExistingArtifactImage(lessonData.artifact_image);
          }
        } else {
          setError("Lesson not found");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lesson:", error);
        setError("Failed to load lesson");
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  // Fetch lesson tasks
  useEffect(() => {
    const fetchLessonTasks = async () => {
      if (!lesson || lesson.id === "new") return;

      try {
        const lessonTasksRef = collection(db, "node_lesson_tasks");
        const lessonRef = doc(db, "node_lessons", lesson.id);
        const q = query(
          lessonTasksRef,
          where("lesson_id", "==", lessonRef),
          orderBy("order", "asc")
        );
        const querySnapshot = await getDocs(q);

        const tasksData = [];
        querySnapshot.forEach((doc) => {
          const taskData = {
            id: doc.id,
            ...doc.data(),
          };
          tasksData.push(taskData);
        });

        setLessonTasks(tasksData);
      } catch (error) {
        console.error("Error fetching lesson tasks:", error);
      }
    };

    fetchLessonTasks();
  }, [lesson]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setCoverImage(null);
    setCoverImagePreview(existingCoverImage || "");
  };

  const handleArtifactFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setArtifactImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setArtifactImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearArtifactImage = () => {
    setArtifactImage(null);
    setArtifactImagePreview(existingArtifactImage || "");
  };

  const handleTaskImageUpload = async (event, isEditing = false) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const timestamp = Date.now();
        const fileName = `images/lesson_tasks/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, file);
        const imageUrl = await getDownloadURL(uploadResult.ref);

        if (isEditing) {
          setEditingTaskData({
            ...editingTaskData,
            image_path: imageUrl,
          });
        } else {
          setNewTaskData({
            ...newTaskData,
            image_path: imageUrl,
          });
        }

        toast.success("Image uploaded successfully!");
      } catch (error) {
        console.error("Error uploading task image:", error);
        toast.error("Error uploading image. Please try again.");
      }
    }
  };

  const handleRemoveTaskImage = (isEditing = false) => {
    if (isEditing) {
      setEditingTaskData({
        ...editingTaskData,
        image_path: "",
      });
    } else {
      setNewTaskData({
        ...newTaskData,
        image_path: "",
      });
    }
  };

  const createLessonIfNeeded = async () => {
    if (lesson && lesson.id !== "new") {
      return lesson.id;
    }

    try {
      let coverImageUrl = existingCoverImage;
      let artifactImageUrl = existingArtifactImage;

      if (coverImage) {
        const timestamp = Date.now();
        const fileName = `images/lessons/${timestamp}_${coverImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, coverImage);
        coverImageUrl = await getDownloadURL(uploadResult.ref);
      }

      if (artifactImage) {
        const timestamp = Date.now();
        const fileName = `images/artifacts/${timestamp}_${artifactImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, artifactImage);
        artifactImageUrl = await getDownloadURL(uploadResult.ref);
      }

      const lessonData = {
        title: title || "Untitled Lesson",
        description: description || "",
        units: topics,
        age_groups: ageGroups,
        activity_instructions: guideInstructions || "",
        cover_image: coverImageUrl,
        artifact_image: artifactImageUrl,
        isPreview: isPreview,
        created_at: new Date(),
        updated_at: new Date(),
      };

      const lessonsRef = collection(db, "node_lessons");
      const docRef = await addDoc(lessonsRef, lessonData);

      // Update the lesson state with the new ID
      setLesson({ id: docRef.id, ...lessonData });

      toast.success("Lesson created successfully!");
      return docRef.id;
    } catch (error) {
      console.error("Error creating lesson:", error);
      toast.error("Error creating lesson. Please try again.");
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      let coverImageUrl = existingCoverImage; // Preserve existing image URL
      let artifactImageUrl = existingArtifactImage; // Preserve existing artifact image URL

      // Upload cover image if a new one is selected
      if (coverImage) {
        const timestamp = Date.now();
        const fileName = `images/lessons/${timestamp}_${coverImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, coverImage);
        coverImageUrl = await getDownloadURL(uploadResult.ref);
      }

      // Upload artifact image if a new one is selected
      if (artifactImage) {
        const timestamp = Date.now();
        const fileName = `images/artifacts/${timestamp}_${artifactImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, artifactImage);
        artifactImageUrl = await getDownloadURL(uploadResult.ref);
      }

      const lessonData = {
        title,
        description,
        units: topics,
        age_groups: ageGroups,
        activity_instructions: guideInstructions,
        cover_image: coverImageUrl,
        artifact_image: artifactImageUrl,
        isPreview: isPreview,
        updated_at: new Date(),
      };

      // Determine if this is a new lesson or existing lesson
      let isNewLesson = lesson.id === "new";

      // If we have a lesson ID that's not "new", verify the lesson exists
      if (!isNewLesson && lesson.id) {
        try {
          const lessonRef = doc(db, "node_lessons", lesson.id);
          const lessonSnap = await getDoc(lessonRef);
          isNewLesson = !lessonSnap.exists();

          if (isNewLesson) {
          }
        } catch (error) {
          console.error("Error checking lesson existence:", error);
          // If we can't verify, assume it's new to be safe
          isNewLesson = true;
        }
      }

      if (isNewLesson) {
        // Create new lesson document

        const lessonsRef = collection(db, "node_lessons");
        const docRef = await addDoc(lessonsRef, {
          ...lessonData,
          created_at: new Date(),
        });

        // Update the lesson state with the new ID
        setLesson({ id: docRef.id, ...lessonData, created_at: new Date() });

        toast.success("Lesson saved successfully!");
      } else {
        // Update existing lesson document
        const lessonRef = doc(db, "node_lessons", lesson.id);
        await updateDoc(lessonRef, lessonData);

        toast.success("Lesson updated successfully!");
      }
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error("Error saving lesson. Please try again.");
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    // Clear video cache when leaving lesson
    setCachedVideos([]);
    navigate("/admin/lessons");
  };

  const handleSaveAndNew = async () => {
    await handleSave();
    // Reset form for new lesson
    setTitle("");
    setDescription("");
    setTopics([]);
    setAgeGroups([]);
    setGuideInstructions("");
    setIsPreview(true);
    setCoverImage(null);
    setCoverImagePreview("");
    setExistingCoverImage("");
    setArtifactImage(null);
    setArtifactImagePreview("");
    setExistingArtifactImage("");
    setLesson({ id: "new" });
    setActiveTab("lesson");
    // Clear video cache for new lesson
    setCachedVideos([]);
  };

  const handleCancel = () => {
    const hasChanges =
      title ||
      description ||
      topics.length > 0 ||
      ageGroups.length > 0 ||
      guideInstructions ||
      coverImage ||
      artifactImage;

    if (hasChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmed) {
        return;
      }
    }

    // Clear video cache when leaving lesson
    setCachedVideos([]);
    navigate("/admin/lessons");
  };

  // Task type definitions
  const taskTypes = [
    {
      value: "discussion_question",
      label: "Discussion Question",
      media_type: "image",
      defaultTitle: "Discussion Question",
    },
    {
      value: "video",
      label: "Video",
      media_type: "video",
      defaultTitle: "Video",
    },
    {
      value: "knowledge_check",
      label: "Knowledge Check",
      media_type: "image",
      defaultTitle: "Knowledge Check",
    },
    {
      value: "engagement_question",
      label: "Engagement Question",
      media_type: "image",
      defaultTitle: "Engagement Question",
    },
    {
      value: "activity_introduction",
      label: "Activity Introduction",
      media_type: "image",
      defaultTitle: "Activity Introduction",
    },
    {
      value: "activity_instructions",
      label: "Activity Instructions",
      media_type: "image",
      defaultTitle: "Activity Instructions",
    },
  ];

  const handleNewTask = () => {
    setNewTaskMode(true);
    setNewTaskData({
      media_type: "",
      title: "",
      text_detail: "",
      image_path: "",
      video_url: "",
      text_alignment: "none",
    });
  };

  const handleTaskTypeChange = (taskType) => {
    const selectedType = taskTypes.find((type) => type.value === taskType);
    if (selectedType) {
      setNewTaskData({
        ...newTaskData,
        media_type: selectedType.media_type,
        title: selectedType.defaultTitle,
      });
    }
  };

  const handleSaveTask = async () => {
    try {
      // Create lesson if needed
      const lessonId = await createLessonIfNeeded();

      // Prepare the task data based on media type
      const taskData = {
        lesson_id: doc(db, "node_lessons", lessonId),
        media_type: newTaskData.media_type,
        title: newTaskData.title,
        image_path: newTaskData.image_path,
        video_url: newTaskData.video_url,
        text_alignment: newTaskData.text_alignment || "none",
        order: lessonTasks.length,
        created_at: new Date(),
      };

      // Save text content to the appropriate field based on media type
      if (newTaskData.media_type === "image") {
        taskData.image_text = newTaskData.text_detail;
      } else if (newTaskData.media_type === "text") {
        taskData.text_detail = newTaskData.text_detail;
      }

      const tasksRef = collection(db, "node_lesson_tasks");
      await addDoc(tasksRef, taskData);

      toast.success("Task saved successfully!");
      setNewTaskMode(false);
      setNewTaskData({
        media_type: "",
        title: "",
        text_detail: "",
        image_path: "",
        video_url: "",
        text_alignment: "none",
      });

      // Refresh tasks
      const updatedTasks = await fetchLessonTasks();
      setLessonTasks(updatedTasks);
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Error saving task. Please try again.");
    }
  };

  const handleSaveTaskAndNew = async () => {
    try {
      // Create lesson if needed
      const lessonId = await createLessonIfNeeded();

      // Prepare the task data based on media type
      const taskData = {
        lesson_id: doc(db, "node_lessons", lessonId),
        media_type: newTaskData.media_type,
        title: newTaskData.title,
        image_path: newTaskData.image_path,
        video_url: newTaskData.video_url,
        text_alignment: newTaskData.text_alignment || "none",
        order: lessonTasks.length,
        created_at: new Date(),
      };

      // Save text content to the appropriate field based on media type
      if (newTaskData.media_type === "image") {
        taskData.image_text = newTaskData.text_detail;
      } else if (newTaskData.media_type === "text") {
        taskData.text_detail = newTaskData.text_detail;
      }

      const tasksRef = collection(db, "node_lesson_tasks");
      await addDoc(tasksRef, taskData);

      toast.success("Task saved successfully!");

      // Reset form for new task but stay in new task mode
      setNewTaskData({
        media_type: "",
        title: "",
        text_detail: "",
        image_path: "",
        video_url: "",
        text_alignment: "none",
      });

      // Refresh tasks
      const updatedTasks = await fetchLessonTasks();
      setLessonTasks(updatedTasks);
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Error saving task. Please try again.");
    }
  };

  const handleCancelTask = () => {
    setNewTaskMode(false);
    setNewTaskData({
      media_type: "",
      title: "",
      text_detail: "",
      image_path: "",
      video_url: "",
      text_alignment: "none",
    });
  };

  const handleEditTask = (taskId) => {
    const task = lessonTasks.find((t) => t.id === taskId);
    if (task) {
      setEditingTaskId(taskId);
      setEditingTaskData({
        media_type: task.media_type || "",
        title: task.title || "",
        text_detail:
          task.media_type === "image"
            ? task.image_text || ""
            : task.text_detail || "",
        image_path: task.image_path || "",
        video_url: task.video_url || "",
        text_alignment: task.text_alignment || "none",
      });
    }
  };

  const handleSaveEditTask = async () => {
    if (!editingTaskId) return;

    try {
      const taskRef = doc(db, "node_lesson_tasks", editingTaskId);

      // Prepare the data to save based on media type
      const updateData = {
        media_type: editingTaskData.media_type,
        title: editingTaskData.title,
        image_path: editingTaskData.image_path,
        video_url: editingTaskData.video_url,
        text_alignment: editingTaskData.text_alignment || "none",
        updated_at: new Date(),
      };

      // Save text content to the appropriate field based on media type
      if (editingTaskData.media_type === "image") {
        updateData.image_text = editingTaskData.text_detail;
      } else if (editingTaskData.media_type === "text") {
        updateData.text_detail = editingTaskData.text_detail;
      }

      await updateDoc(taskRef, updateData);

      toast.success("Task updated successfully!");
      setEditingTaskId(null);
      setEditingTaskData({
        media_type: "",
        title: "",
        text_detail: "",
        image_path: "",
        video_url: "",
        text_alignment: "none",
      });

      // Refresh tasks
      const updatedTasks = await fetchLessonTasks();
      setLessonTasks(updatedTasks);
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error updating task. Please try again.");
    }
  };

  const handleSaveEditTaskAndNew = async () => {
    if (!editingTaskId) return;

    try {
      const taskRef = doc(db, "node_lesson_tasks", editingTaskId);

      // Prepare the data to save based on media type
      const updateData = {
        media_type: editingTaskData.media_type,
        title: editingTaskData.title,
        image_path: editingTaskData.image_path,
        video_url: editingTaskData.video_url,
        text_alignment: editingTaskData.text_alignment || "none",
        updated_at: new Date(),
      };

      // Save text content to the appropriate field based on media type
      if (editingTaskData.media_type === "image") {
        updateData.image_text = editingTaskData.text_detail;
      } else if (editingTaskData.media_type === "text") {
        updateData.text_detail = editingTaskData.text_detail;
      }

      await updateDoc(taskRef, updateData);

      toast.success("Task updated successfully!");

      // Exit edit mode and start new task mode
      setEditingTaskId(null);
      setEditingTaskData({
        media_type: "",
        title: "",
        text_detail: "",
        image_path: "",
        video_url: "",
        text_alignment: "none",
      });

      // Start new task mode
      setNewTaskMode(true);
      setNewTaskData({
        media_type: "",
        title: "",
        text_detail: "",
        image_path: "",
        video_url: "",
        text_alignment: "none",
      });

      // Refresh tasks
      const updatedTasks = await fetchLessonTasks();
      setLessonTasks(updatedTasks);
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error updating task. Please try again.");
    }
  };

  const handleCancelEditTask = () => {
    setEditingTaskId(null);
    setEditingTaskData({
      media_type: "",
      title: "",
      text_detail: "",
      image_path: "",
      video_url: "",
      text_alignment: "none",
    });
  };

  const handleDeleteTask = (taskId) => {
    const task = lessonTasks.find((t) => t.id === taskId);
    setTaskToDelete(task);
    setShowDeleteTaskModal(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      const taskRef = doc(db, "node_lesson_tasks", taskToDelete.id);
      await deleteDoc(taskRef);

      // Remove from local state
      setLessonTasks(lessonTasks.filter((task) => task.id !== taskToDelete.id));

      toast.success("Task deleted successfully!");
      setShowDeleteTaskModal(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Error deleting task. Please try again.");
    }
  };

  const cancelDeleteTask = () => {
    setShowDeleteTaskModal(false);
    setTaskToDelete(null);
  };

  const openVideoDialog = async () => {
    if (!title.trim()) {
      toast.error("Please enter a lesson title first to search for videos.");
      return;
    }

    setShowVideoDialog(true);

    // If we have cached videos, use them
    if (cachedVideos.length > 0) {
      const lessonTitle = title.toLowerCase();
      const filtered = cachedVideos.filter((video) =>
        video.name.toLowerCase().includes(lessonTitle)
      );
      setFilteredVideos(filtered);
      return;
    }

    // Otherwise, fetch videos from API
    setLoadingVideos(true);

    try {
      const lessonTitle = title.toLowerCase();
      let allVideos = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await fetch(
          `https://api.vimeo.com/users/129001864/projects/21885235/videos?page=${page}&per_page=100`,
          {
            headers: {
              Authorization: "Bearer 71784e0083b4a9fccd6b1cb1ec310a0f",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const videosList = data.data.map((video) => ({
          id: video.uri.split("/").pop(),
          name: video.name,
          description: video.description,
          player_url: video.player_embed_url,
          duration: video.duration,
          created_time: video.created_time,
        }));

        allVideos = [...allVideos, ...videosList];

        // Check if there are more pages
        if (data.paging && data.paging.next) {
          page++;
        } else {
          hasMorePages = false;
        }
      }

      // Cache all videos for this lesson session
      setCachedVideos(allVideos);

      // Filter videos by title only
      const filtered = allVideos.filter((video) =>
        video.name.toLowerCase().includes(lessonTitle)
      );

      setFilteredVideos(filtered);
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error("Error loading videos. Please try again.");
    } finally {
      setLoadingVideos(false);
    }
  };

  const selectVideo = (video) => {
    const videoUrl = video.player_url;

    if (newTaskMode) {
      setNewTaskData({
        ...newTaskData,
        video_url: videoUrl,
        title: video.name,
      });
    } else if (editingTaskId) {
      setEditingTaskData({
        ...editingTaskData,
        video_url: videoUrl,
        title: video.name,
      });
    }

    setShowVideoDialog(false);
    toast.success("Video selected successfully!");
  };

  const closeVideoDialog = () => {
    setShowVideoDialog(false);
  };

  const handleAddVideos = async () => {
    if (!title.trim()) {
      toast.error("Please enter a lesson title first to search for videos.");
      return;
    }

    try {
      const lessonTitle = title.toLowerCase();
      let allVideos = [];

      // Use cached videos if available, otherwise fetch from API
      if (cachedVideos.length > 0) {
        allVideos = cachedVideos;
      } else {
        let page = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await fetch(
            `https://api.vimeo.com/users/129001864/projects/21885235/videos?page=${page}&per_page=100`,
            {
              headers: {
                Authorization: "Bearer 71784e0083b4a9fccd6b1cb1ec310a0f",
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const videosList = data.data.map((video) => ({
            id: video.uri.split("/").pop(),
            name: video.name,
            description: video.description,
            player_url: video.player_embed_url,
            duration: video.duration,
            created_time: video.created_time,
          }));

          allVideos = [...allVideos, ...videosList];

          if (data.paging && data.paging.next) {
            page++;
          } else {
            hasMorePages = false;
          }
        }

        // Cache the videos for future use
        setCachedVideos(allVideos);
      }

      // Filter videos by title and sort alphabetically
      const matchingVideos = allVideos
        .filter((video) => video.name.toLowerCase().includes(lessonTitle))
        .sort((a, b) => a.name.localeCompare(b.name));

      if (matchingVideos.length === 0) {
        toast.error(
          `No videos found matching "${title}". Try a different lesson title.`
        );
        return;
      }

      setVideosToAdd(matchingVideos);
      setShowAddVideosDialog(true);
    } catch (error) {
      console.error("Error fetching videos for bulk add:", error);
      toast.error("Error loading videos. Please try again.");
    }
  };

  const confirmAddVideos = async () => {
    try {
      // Create lesson if needed
      const lessonId = await createLessonIfNeeded();

      const tasksRef = collection(db, "node_lesson_tasks");
      const batch = writeBatch(db);

      // Add each video as a task
      videosToAdd.forEach((video, index) => {
        const videoUrl = video.player_url;
        const taskData = {
          lesson_id: doc(db, "node_lessons", lessonId),
          media_type: "video",
          title: `Video ${index + 1} of ${videosToAdd.length}`,
          text_detail: "",
          image_path: "",
          video_url: videoUrl,
          order: lessonTasks.length + index,
          created_at: new Date(),
        };

        const taskRef = doc(tasksRef);
        batch.set(taskRef, taskData);
      });

      await batch.commit();

      toast.success(`Added ${videosToAdd.length} video tasks successfully!`);
      setShowAddVideosDialog(false);
      setVideosToAdd([]);

      // Refresh tasks
      const updatedTasks = await fetchLessonTasks();
      setLessonTasks(updatedTasks);
    } catch (error) {
      console.error("Error adding video tasks:", error);
      toast.error("Error adding video tasks. Please try again.");
    }
  };

  const cancelAddVideos = () => {
    setShowAddVideosDialog(false);
    setVideosToAdd([]);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = lessonTasks.findIndex((task) => task.id === active.id);
      const newIndex = lessonTasks.findIndex((task) => task.id === over.id);

      const newTasks = arrayMove(lessonTasks, oldIndex, newIndex);
      setLessonTasks(newTasks);

      // Update order in Firestore
      try {
        const batch = writeBatch(db);

        newTasks.forEach((task, index) => {
          const taskRef = doc(db, "node_lesson_tasks", task.id);
          batch.update(taskRef, { order: index });
        });

        await batch.commit();
        toast.success("Task order updated successfully!");
      } catch (error) {
        console.error("Error updating task order:", error);
        toast.error("Error updating task order. Please try again.");
      }
    }
  };

  const fetchLessonTasks = async () => {
    if (!lesson || lesson.id === "new") return [];

    try {
      const lessonTasksRef = collection(db, "node_lesson_tasks");
      const lessonRef = doc(db, "node_lessons", lesson.id);
      const q = query(
        lessonTasksRef,
        where("lesson_id", "==", lessonRef),
        orderBy("order", "asc")
      );
      const querySnapshot = await getDocs(q);

      const tasksData = [];
      querySnapshot.forEach((doc) => {
        tasksData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setLessonTasks(tasksData);
      return tasksData;
    } catch (error) {
      console.error("Error fetching lesson tasks:", error);
      return [];
    }
  };

  const handleOpenPreview = (taskData) => {
    setPreviewTaskData(taskData);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewTaskData(null);
  };

  const canPreview = (taskData) => {
    if (!taskData.media_type) return false;

    if (taskData.media_type === "video") {
      return !!taskData.video_url;
    }

    if (taskData.media_type === "image" || taskData.media_type === "text") {
      return !!taskData.text_detail || !!taskData.image_path;
    }

    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading lesson...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">
              {lesson?.id === "new" ? "New Lesson" : "Edit Lesson"}
            </h1>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("lesson")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "lesson"
                    ? "border-primary-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Lesson
              </button>
              <button
                onClick={() => setActiveTab("guide-instructions")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "guide-instructions"
                    ? "border-primary-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Activity Prep
              </button>
              <button
                onClick={() => setActiveTab("tasks")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "tasks"
                    ? "border-primary-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Tasks
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <>
            {activeTab === "lesson" && (
              <div className="flex">
                {/* Left Column */}
                <div className="flex-1 pr-8">
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter lesson title"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter lesson description"
                      />
                    </div>

                    {/* Topics */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Topics
                      </label>
                      <MultiSelect
                        options={topicsData}
                        selectedValues={topics}
                        onChange={setTopics}
                        placeholder="Please select topics..."
                      />
                    </div>

                    {/* Age Groups */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age Groups
                      </label>
                      <MultiSelect
                        options={ageGroupsData}
                        selectedValues={ageGroups}
                        onChange={setAgeGroups}
                        placeholder="Please select age groups..."
                      />
                    </div>

                    {/* Preview Mode */}
                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isPreview"
                          checked={isPreview}
                          onChange={(e) => setIsPreview(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="isPreview"
                          className="ml-2 block text-sm text-gray-700"
                        >
                          Preview Mode
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Preview lessons are only visible to Super Admins.
                        Uncheck to make this lesson visible to users and admins.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="w-1/2 pl-8">
                  <div className="space-y-6">
                    {/* Upload Cover */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="cover-upload"
                      />
                      <div className="flex space-x-2">
                        <label
                          htmlFor="cover-upload"
                          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors cursor-pointer inline-block"
                        >
                          Upload Cover
                        </label>
                        {(coverImage || existingCoverImage) && (
                          <button
                            type="button"
                            onClick={handleClearImage}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="mt-2 w-[360px] h-[233px] bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
                        {coverImagePreview ? (
                          <img
                            src={coverImagePreview}
                            alt="Cover preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-500 text-xs">
                            No image selected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Upload Artifact */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Artifact Image
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        This image will be shown to users when they complete
                        this lesson.
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleArtifactFileUpload}
                        className="hidden"
                        id="artifact-upload"
                      />
                      <div className="flex space-x-2">
                        <label
                          htmlFor="artifact-upload"
                          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors cursor-pointer inline-block"
                        >
                          Upload Artifact
                        </label>
                        {(artifactImage || existingArtifactImage) && (
                          <button
                            type="button"
                            onClick={handleClearArtifactImage}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="mt-2 w-[360px] h-[233px] bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
                        {artifactImagePreview ? (
                          <img
                            src={artifactImagePreview}
                            alt="Artifact preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-500 text-xs">
                            No artifact selected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "tasks" && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Lesson Tasks
                  </h3>
                  <div className="flex items-center space-x-2">
                    {/* Task Menu Dropdown */}
                    <div className="relative task-menu-container">
                      <button
                        onClick={() => setShowTaskMenu(!showTaskMenu)}
                        disabled={newTaskMode || editingTaskId}
                        className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                          newTaskMode || editingTaskId
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gray-600 text-white hover:bg-gray-700"
                        }`}
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                        <span>Options</span>
                      </button>

                      {showTaskMenu && !newTaskMode && !editingTaskId && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                handleAddVideos();
                                setShowTaskMenu(false);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Add Videos
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleNewTask}
                      disabled={newTaskMode || editingTaskId}
                      className={`px-4 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                        newTaskMode || editingTaskId
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>New Task</span>
                    </button>
                  </div>
                </div>

                {/* New Task Row */}
                {newTaskMode && (
                  <div className="border border-gray-200 rounded-md p-4 mb-4 bg-gray-50">
                    {/* First Row: Task Type, Title, and Action Buttons */}
                    <div className="flex items-center space-x-4 mb-4">
                      {/* Task Type Dropdown */}
                      <div className="w-1/4">
                        <select
                          value={
                            newTaskData.media_type
                              ? taskTypes.find(
                                  (t) => t.media_type === newTaskData.media_type
                                )?.value || ""
                              : ""
                          }
                          onChange={(e) => handleTaskTypeChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Select task type...</option>
                          {taskTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Title Input */}
                      <div className="w-1/4">
                        <input
                          type="text"
                          value={newTaskData.title}
                          onChange={(e) =>
                            setNewTaskData({
                              ...newTaskData,
                              title: e.target.value,
                            })
                          }
                          placeholder="Task title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Spacer */}
                      <div className="flex-1"></div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveTaskAndNew}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                          Save & New
                        </button>
                        <button
                          onClick={handleSaveTask}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelTask}
                          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>

                    {/* Second Row: Text Detail, Text Preview, Image Upload/Preview */}
                    {(newTaskData.media_type === "image" ||
                      newTaskData.media_type === "text") && (
                      <>
                        <div className="flex space-x-4 h-64">
                          {/* Text Detail */}
                          <div className="w-2/5">
                            <textarea
                              value={newTaskData.text_detail}
                              onChange={(e) =>
                                setNewTaskData({
                                  ...newTaskData,
                                  text_detail: e.target.value,
                                })
                              }
                              placeholder="Text Detail"
                              className="w-full h-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            />
                          </div>

                          {/* Text Preview */}
                          <div className="w-2/5">
                            <div className="w-full h-full bg-gray-50 rounded border border-gray-200 p-2 overflow-y-auto">
                              {newTaskData.text_detail ? (
                                <div
                                  className={`prose prose-sm max-w-none ${getTextAlignment(
                                    newTaskData.text_alignment,
                                    newTaskData.text_detail
                                  )}`}
                                >
                                  <ReactMarkdown
                                    components={getMarkdownComponents()}
                                  >
                                    {newTaskData.text_detail}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400 text-center">
                                  Text preview will appear here...
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Image Preview and Upload */}
                          <div className="w-1/5">
                            <div className="space-y-2 h-full flex flex-col">
                              {/* Image Preview Area */}
                              <div className="flex-1 bg-gray-200 rounded border-2 border-dashed border-gray-300 flex items-center justify-center relative">
                                {newTaskData.image_path ? (
                                  <>
                                    <img
                                      src={newTaskData.image_path}
                                      alt="Task preview"
                                      className="w-full h-full object-cover rounded"
                                    />
                                    <button
                                      onClick={() =>
                                        handleRemoveTaskImage(false)
                                      }
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold hover:bg-red-600 shadow-lg"
                                    >
                                      ×
                                    </button>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    Image preview
                                  </span>
                                )}
                              </div>

                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleTaskImageUpload(e, false)
                                }
                                className="hidden"
                                id="task-image-upload"
                              />
                              <label
                                htmlFor="task-image-upload"
                                className="bg-primary-600 text-white px-3 py-2 rounded text-sm hover:bg-primary-700 transition-colors cursor-pointer text-center"
                              >
                                Upload Image
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Third Row: Text Alignment Controls and Preview */}
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() =>
                                setNewTaskData({
                                  ...newTaskData,
                                  text_alignment: "none",
                                })
                              }
                              title="No Alignment (uses word count: <30 words = center, ≥30 words = left, or bullets = left)"
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                newTaskData.text_alignment === "none"
                                  ? "bg-primary-600 text-white"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                            >
                              None
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setNewTaskData({
                                  ...newTaskData,
                                  text_alignment: "center",
                                })
                              }
                              title="Center Alignment"
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                newTaskData.text_alignment === "center"
                                  ? "bg-primary-600 text-white"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                            >
                              Center
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setNewTaskData({
                                  ...newTaskData,
                                  text_alignment: "left",
                                })
                              }
                              title="Left Alignment"
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                newTaskData.text_alignment === "left"
                                  ? "bg-primary-600 text-white"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                            >
                              Left
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPreview(newTaskData)}
                            disabled={!canPreview(newTaskData)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              canPreview(newTaskData)
                                ? "bg-primary-600 text-white hover:bg-primary-700"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            }`}
                          >
                            Preview
                          </button>
                        </div>
                      </>
                    )}

                    {/* Video Mode */}
                    {newTaskData.media_type === "video" && (
                      <div className="flex space-x-4 h-64">
                        <div className="w-1/2">
                          <button
                            type="button"
                            onClick={openVideoDialog}
                            className="w-full h-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left bg-white hover:bg-gray-50 flex items-center justify-center"
                          >
                            {newTaskData.video_url
                              ? "Change Video"
                              : "Select Video..."}
                          </button>
                        </div>
                        <div className="w-1/2">
                          {newTaskData.video_url && (
                            <div className="w-full h-full bg-gray-50 rounded border border-gray-200 p-2 flex items-center justify-center">
                              <div className="text-sm text-gray-600">
                                Video selected
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Existing Tasks */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={lessonTasks.map((task) => task.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {lessonTasks.map((task) => (
                        <SortableTask
                          key={task.id}
                          task={task}
                          isEditing={editingTaskId === task.id}
                          editingTaskData={editingTaskData}
                          setEditingTaskData={setEditingTaskData}
                          handleEditTask={handleEditTask}
                          handleDeleteTask={handleDeleteTask}
                          handleSaveEditTask={handleSaveEditTask}
                          handleSaveEditTaskAndNew={handleSaveEditTaskAndNew}
                          handleCancelEditTask={handleCancelEditTask}
                          handleTaskImageUpload={handleTaskImageUpload}
                          handleRemoveTaskImage={handleRemoveTaskImage}
                          taskTypes={taskTypes}
                          openVideoDialog={openVideoDialog}
                          handleOpenPreview={handleOpenPreview}
                          canPreview={canPreview}
                        />
                      ))}
                      {lessonTasks.length === 0 && !newTaskMode && (
                        <p className="text-gray-500 text-center py-4">
                          No tasks found for this lesson.
                        </p>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {activeTab === "guide-instructions" && (
              <div className="flex">
                {/* Left Column */}
                <div className="flex-1 pr-8">
                  <div>
                    <textarea
                      value={guideInstructions}
                      onChange={(e) => setGuideInstructions(e.target.value)}
                      rows={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter guide instructions in markdown format..."
                    />
                  </div>
                </div>

                {/* Right Column - Preview */}
                <div className="w-1/2 pl-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Preview
                  </h3>
                  <div className="prose prose-gray max-w-none p-4 border border-gray-200 rounded-md bg-gray-50">
                    {guideInstructions ? (
                      <ReactMarkdown components={getMarkdownComponents()}>
                        {guideInstructions}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-gray-500">
                        Preview will appear here...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            {newTaskMode || editingTaskId ? (
              <div className="flex items-center space-x-4 w-full">
                <div className="flex-1 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                  <span className="font-medium">⚠️ Task in progress:</span>{" "}
                  Please save or cancel your task before saving the lesson.
                </div>
                {lesson.id === "new" ? (
                  <>
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 px-6 py-2 rounded-md cursor-not-allowed"
                    >
                      Save & Close
                    </button>
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 px-6 py-2 rounded-md cursor-not-allowed"
                    >
                      Save & New
                    </button>
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 px-6 py-2 rounded-md cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 px-6 py-2 rounded-md cursor-not-allowed"
                    >
                      Save
                    </button>
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 px-6 py-2 rounded-md cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {lesson.id === "new" ? (
                  <>
                    <button
                      onClick={handleSaveAndClose}
                      className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save & Close
                    </button>
                    <button
                      onClick={handleSaveAndNew}
                      className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save & New
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSaveAndClose}
                      className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Videos Confirmation Dialog */}
      {showAddVideosDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Add Video Tasks
                </h3>
                <button
                  onClick={cancelAddVideos}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Found{" "}
                  <span className="font-medium">{videosToAdd.length}</span>{" "}
                  videos matching "{title}":
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  These videos will be added as tasks in alphabetical order.
                </p>
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                {videosToAdd.map((video, index) => (
                  <div
                    key={video.id}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">
                        {video.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Duration: {Math.floor(video.duration / 60)}:
                        {(video.duration % 60).toString().padStart(2, "0")} •
                        ID: {video.id}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 ml-2">
                      Video {index + 1} of {videosToAdd.length}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={cancelAddVideos}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAddVideos}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Add {videosToAdd.length} Videos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Selection Dialog */}
      {showVideoDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Select Video
                </h3>
                <button
                  onClick={closeVideoDialog}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing videos that match:{" "}
                  <span className="font-medium">"{title}"</span>
                </p>
                {loadingVideos ? (
                  <div className="flex items-center space-x-2">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    <p className="text-sm text-gray-500">Loading videos...</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Found {filteredVideos.length} matching videos
                  </p>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {loadingVideos ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
                    <p className="text-gray-500">Searching for videos...</p>
                  </div>
                ) : filteredVideos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No videos found matching "{title}". Try a different lesson
                    title.
                  </p>
                ) : (
                  filteredVideos.map((video) => (
                    <div
                      key={video.id}
                      className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => selectVideo(video)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">
                            {video.name}
                          </h4>
                          {video.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {video.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>
                              Duration: {Math.floor(video.duration / 60)}:
                              {(video.duration % 60)
                                .toString()
                                .padStart(2, "0")}
                            </span>
                            <span>ID: {video.id}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectVideo(video);
                          }}
                          className="bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={closeVideoDialog}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Task Confirmation Modal */}
      {showDeleteTaskModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Task
                </h3>
                <button
                  onClick={cancelDeleteTask}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mt-2">
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete "{taskToDelete?.title}"? This
                  action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteTask}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteTask}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Preview Modal */}
      {showPreviewModal && previewTaskData && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50"
          onClick={handleClosePreview}
        >
          <div
            className="min-h-screen bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="flex justify-end p-6">
              <button
                onClick={handleClosePreview}
                className="bg-primary-500 text-white p-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content Area */}
            <div className="px-3 sm:px-6 pb-24">
              <div className="max-w-4xl mx-auto">
                <div className="rounded-lg p-4 sm:p-8 pt-4 min-h-[450px] bg-white">
                  {/* Image Type */}
                  {previewTaskData.media_type === "image" && (
                    <div
                      className="rounded-lg px-6 sm:px-14 py-12 pt-8 min-h-[350px]"
                      style={{
                        backgroundImage: `url(${grayBackground})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "auto",
                      }}
                    >
                      {/* Task Title */}
                      <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">
                        {previewTaskData.title}
                      </h2>

                      <div className="flex flex-col gap-3">
                        {/* Text Content */}
                        {previewTaskData.text_detail && (
                          <div
                            className={`text-gray-700 leading-relaxed prose prose-gray max-w-none text-lg ${getTextAlignment(
                              previewTaskData.text_alignment,
                              previewTaskData.text_detail
                            )}`}
                          >
                            <ReactMarkdown
                              components={{
                                ol: ({ children }) => (
                                  <ol className="list-decimal list-inside space-y-1 mb-4">
                                    {children}
                                  </ol>
                                ),
                                ul: ({ children }) => (
                                  <ul className="list-disc list-inside space-y-1 mb-4">
                                    {children}
                                  </ul>
                                ),
                                li: ({ children }) => (
                                  <li className="ml-4">{children}</li>
                                ),
                                strong: ({ children }) => (
                                  <strong className="font-bold text-gray-900">
                                    {children}
                                  </strong>
                                ),
                                p: ({ children }) => (
                                  <p className="mb-4">{children}</p>
                                ),
                              }}
                            >
                              {previewTaskData.text_detail}
                            </ReactMarkdown>
                          </div>
                        )}

                        {/* Image Content */}
                        {previewTaskData.image_path && (
                          <div className="flex justify-center">
                            <img
                              src={previewTaskData.image_path}
                              alt="Lesson content"
                              className="w-full max-w-[90vw] max-h-[400px] h-auto rounded-lg object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Text Type */}
                  {previewTaskData.media_type === "text" && (
                    <div
                      className="rounded-lg px-6 sm:px-12 py-12 pt-8 min-h-[350px]"
                      style={{
                        backgroundImage: `url(${grayBackground})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "auto",
                      }}
                    >
                      {/* Task Title */}
                      <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">
                        {previewTaskData.title}
                      </h2>

                      {/* Text Content */}
                      {previewTaskData.text_detail && (
                        <div
                          className={`text-gray-700 leading-relaxed prose prose-gray max-w-none text-lg ${getTextAlignment(
                            previewTaskData.text_alignment,
                            previewTaskData.text_detail
                          )}`}
                        >
                          <ReactMarkdown
                            components={{
                              ol: ({ children }) => (
                                <ol className="list-decimal list-outside space-y-2">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="mb-4">{children}</li>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc list-inside space-y-1 mb-4">
                                  {children}
                                </ul>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-bold text-gray-900">
                                  {children}
                                </strong>
                              ),
                              p: ({ children }) => (
                                <p className="mb-2">{children}</p>
                              ),
                            }}
                          >
                            {previewTaskData.text_detail}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Type */}
                  {previewTaskData.media_type === "video" && (
                    <div className="flex justify-center">
                      <div className="w-full max-w-[90vw]">
                        <iframe
                          src={previewTaskData.video_url}
                          width="100%"
                          height="400"
                          className="w-full h-[300px] md:h-[400px] lg:h-[500px] xl:h-[600px] rounded-lg"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Lesson video preview"
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonEntry;
